import { ComponentChildren, createContext } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useState } from 'preact/hooks';

import { TourContext } from './types';

interface TourContextValue {
  context: TourContext | undefined;
  publish: (key: keyof TourContext, value: TourContext[keyof TourContext]) => void;
}

const Ctx = createContext<TourContextValue>({ context: undefined, publish: () => {} });

/**
 * Holds the context tours read to branch and to fill copy.
 *
 * Mounted ABOVE the router, because `TourHost` renders inside `PageWrapper` as a sibling that
 * PRECEDES the page's own children — a provider mounted within a page could never reach it.
 *
 * A context change is itself the re-evaluation signal: conditions are recomputed because their
 * consumer re-rendered, so there is no explicit "notify the host" call that a future code path
 * could forget to make. `children` arrives as a stable element from the app root, so updating
 * this state re-renders the consumers (TourHost) rather than the whole tree.
 */
export const TourContextProvider = ({ children }: { children: ComponentChildren }) => {
  const [context, setContext] = useState<TourContext | undefined>(undefined);

  // Stable across renders. If `publish` changed identity with `context`, the effect in
  // useProvideTourContext would re-run on every publish, and its cleanup would clear the value
  // it had just written — an endless clear/rewrite cycle.
  const publish = useCallback<TourContextValue['publish']>(
    (key, entry) =>
      setContext((previous) => {
        if (previous?.[key] === entry) return previous; // no-op writes must not re-render
        const next = { ...previous, [key]: entry };
        // Drop the key entirely when cleared, so `'habitat' in ctx` stays meaningful.
        if (entry === undefined) delete next[key];
        return next;
      }),
    [],
  );

  const value = useMemo<TourContextValue>(() => ({ context, publish }), [context, publish]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/** Read the current tour context. Undefined until a page publishes one. */
export const useTourContext = (): TourContext | undefined => useContext(Ctx).context;

/**
 * Publish one slice of the tour context for as long as the calling component is mounted.
 *
 * Pass a MEMOIZED value: this writes whenever the reference changes, and the habitat context is
 * rebuilt from socket-updated redux state. Clearing on unmount covers leaving for another page;
 * it does NOT cover habitat -> habitat navigation, where the page component stays mounted — the
 * `habitatId` carried in the value is what guards that case.
 */
export const useProvideTourContext = <K extends keyof TourContext>(key: K, value: TourContext[K]): void => {
  const { publish } = useContext(Ctx);

  useEffect(() => {
    publish(key, value);
    return () => publish(key, undefined);
  }, [publish, key, value]);
};
